import {
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiPencil, mdiPlus, mdiTrashCan } from "@mdi/js";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TermsException } from "../../../../@types/services/TermsException";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import {
  deleteTermsException,
  fetchTermsExceptions,
} from "../../../../services/termsExceptions";

const TermsExceptionTableRow: React.FC<{
  termException: TermsException;
  onDelete: () => void;
}> = ({ termException, onDelete }) => {
  const { t } = useTranslation();

  const { user } = useAuth();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);

  const handleDelete = (id: string): void => {
    setLoadingRemove(true);
    deleteTermsException(id)
      .then(() => {
        enqueueSnackbar(
          t("termsExceptions.index.enqueueSnackbar.excludeSuccess"),
          {
            variant: "success",
          },
        );
        onDelete();
      })
      .catch(() => {
        enqueueSnackbar(
          t("termsExceptions.index.enqueueSnackbar.excludeError"),
          {
            variant: "error",
          },
        );
      })
      .finally(() => {
        setLoadingRemove(false);
      });
  };

  const handleEdit = (): void => {
    navigate({
      params: {
        termExceptionId: termException.id,
      },
      to: "/vsupplierregistration/terms_exceptions/$termExceptionId/edit",
    });
  };

  return (
    <TableRow key={termException.id}>
      <TableCell>{termException.vendor.fullvendorname}</TableCell>
      <TableCell>{termException.vendor.sap_code}</TableCell>
      <TableCell>{termException.vendor.taxnumber1}</TableCell>
      <TableCell>
        {new Date(termException.created_at).toLocaleDateString("en-US", {
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          month: "short",
          year: "numeric",
        })}
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            gap: 1,
          }}
        >
          {user?.roles_names.includes("Terms Exception - Update") && (
            <Tooltip title={t("termsExceptions.index.tooltip.edit")}>
              <IconButton onClick={() => handleEdit()}>
                <Icon icon={mdiPencil} />
              </IconButton>
            </Tooltip>
          )}
          {user?.roles_names.includes("Terms Exception - Delete") &&
            (showConfirm ? (
              <Tooltip title={t("termsExceptions.index.tooltip.confirm")}>
                <Button
                  color="error"
                  loading={loadingRemove}
                  loadingPosition="center"
                  onClick={() => handleDelete(termException.id)}
                >
                  {t("termsExceptions.index.tooltip.confirm")}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={t("termsExceptions.index.tooltip.delete")}>
                <IconButton onClick={() => setShowConfirm(true)}>
                  <Icon icon={mdiTrashCan} />
                </IconButton>
              </Tooltip>
            ))}
        </Box>
      </TableCell>
    </TableRow>
  );
};

const TermsExceptionTable: React.FC<{
  termsExceptions: TermsException[];
  onDelete: () => void;
}> = ({ termsExceptions, onDelete }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("termsExceptions.index.name")}</TableCell>
            <TableCell>{t("termsExceptions.index.sapCode")}</TableCell>
            <TableCell>{t("termsExceptions.index.taxNumber")}</TableCell>
            <TableCell>{t("termsExceptions.index.createdAt")}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {termsExceptions.map((termException) => (
            <TermsExceptionTableRow
              key={termException.id}
              onDelete={onDelete}
              termException={termException}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const TermsExceptions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [termsExceptions, setTermsExceptions] = useState<TermsException[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback((): void => {
    setIsLoading(true);
    fetchTermsExceptions()
      .then((response) => {
        if (response.results === null) {
          setTermsExceptions([]);
        } else {
          setTermsExceptions(response.results);
        }
      })
      .catch(() => {
        enqueueSnackbar(t("termsExceptions.enqueueSnackbar.fetchError"), {
          variant: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <PageContainer maxWidth="xl" title={t("termsExceptions.index.title")}>
        <CircularProgress />
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="xl" title={t("termsExceptions.index.title")}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          padding: "24px",
          width: "100%",
        }}
      >
        {user?.roles_names.includes("Terms Exception - Create") && (
          <Button
            onClick={() =>
              navigate({ to: "/vsupplierregistration/terms_exceptions/new" })
            }
            sx={{ width: "fit-content" }}
            variant="contained"
          >
            <Icon icon={mdiPlus} />
            {t("termsExceptions.index.button.create")}
          </Button>
        )}

        {termsExceptions.length > 0 ? (
          <TermsExceptionTable
            onDelete={fetchData}
            termsExceptions={termsExceptions}
          />
        ) : (
          <Box
            sx={{
              color: "text.secondary",
              padding: "32px",
              textAlign: "center",
            }}
          >
            {t("termsExceptions.index.noTermsExceptions")}
          </Box>
        )}
      </Paper>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vsupplierregistration/terms_exceptions/",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Terms Exception - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: TermsExceptions,
});
